import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Shield, User, UserCog, LogIn } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();

  const navItems = {
    fr: [
      { name: 'Accueil', path: '/' },
      { name: 'Services', path: '/services' },
      { name: 'Blog', path: '/blog' },
      { name: 'Contact', path: '/contact' }
    ],
    en: [
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/services' },
      { name: 'Blog', path: '/blog' },
      { name: 'Contact', path: '/contact' }
    ]
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="bg-gradient-to-br from-blue-900 to-blue-700 p-2 rounded-lg transition-transform group-hover:scale-105">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">
              {language === 'fr' ? 'DépôtSecure' : 'DepositSecure'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems[language].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors relative group ${
                  isActive(item.path)
                    ? 'text-blue-900'
                    : 'text-slate-600 hover:text-blue-900'
                }`}
              >
                {item.name}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-blue-900 transform origin-left transition-transform ${
                    isActive(item.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Language Toggle & Access Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="text-sm font-medium text-slate-600 hover:text-blue-900"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
            
            {/* Access Dropdown */}
            <div className="relative group">
              <Button
                variant="outline"
                className="text-sm font-medium text-slate-600 hover:text-blue-900 hover:border-blue-900"
              >
                <LogIn className="h-4 w-4 mr-2" />
                {language === 'fr' ? 'Connexion' : 'Login'}
              </Button>
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-2">
                  <Link
                    to="/client/login"
                    className="flex items-center px-4 py-3 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                  >
                    <User className="h-4 w-4 mr-3 text-blue-600" />
                    <div>
                      <div className="font-medium">{language === 'fr' ? 'Espace Client' : 'Client Area'}</div>
                      <div className="text-xs text-slate-500">{language === 'fr' ? 'Accédez à vos documents' : 'Access your documents'}</div>
                    </div>
                  </Link>
                  <Link
                    to="/admin/login"
                    className="flex items-center px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors border-t"
                  >
                    <UserCog className="h-4 w-4 mr-3 text-slate-600" />
                    <div>
                      <div className="font-medium">{language === 'fr' ? 'Espace Admin' : 'Admin Area'}</div>
                      <div className="text-xs text-slate-500">{language === 'fr' ? 'Gestion du site' : 'Site management'}</div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            
            <Button
              asChild
              className="bg-blue-900 hover:bg-blue-800 text-white"
            >
              <Link to="/contact">
                {language === 'fr' ? 'Commencer' : 'Get Started'}
              </Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {navItems[language].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-lg text-base font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-50 text-blue-900'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-blue-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            <div className="pt-4 border-t border-slate-200 space-y-2">
              <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {language === 'fr' ? 'Connexion' : 'Login'}
              </p>
              <Link
                to="/client/login"
                onClick={() => setIsOpen(false)}
                className="flex items-center px-3 py-2 rounded-lg text-base font-medium text-slate-600 hover:bg-blue-50 hover:text-blue-900 transition-colors"
              >
                <User className="h-5 w-5 mr-3 text-blue-600" />
                {language === 'fr' ? 'Espace Client' : 'Client Area'}
              </Link>
              <Link
                to="/admin/login"
                onClick={() => setIsOpen(false)}
                className="flex items-center px-3 py-2 rounded-lg text-base font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
              >
                <UserCog className="h-5 w-5 mr-3 text-slate-600" />
                {language === 'fr' ? 'Espace Admin' : 'Admin Area'}
              </Link>
            </div>
            
            <div className="pt-4 space-y-2">
              <Button
                variant="outline"
                onClick={toggleLanguage}
                className="w-full"
              >
                {language === 'fr' ? 'English' : 'Français'}
              </Button>
              <Button
                asChild
                className="w-full bg-blue-900 hover:bg-blue-800 text-white"
              >
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  {language === 'fr' ? 'Commencer' : 'Get Started'}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
